import React from "react";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { useChallengeReview } from "../../services/moodle/duel.queries";
import aduanaTheme from "../../components/DuelSkin/aduanaTheme";
import "../../components/DuelSkin/aduanaSkin.css";

const { brand, labels } = aduanaTheme;

const RESULT_LABEL = {
  won: labels.historyResultWonLabel,
  lost: labels.historyResultLostLabel,
  draw: labels.historyResultDrawLabel,
};

/**
 * DuelReviewModal — presentational modal displaying question-by-question review
 * of a completed duel ("Acta del operativo").
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {number|string} props.challengeId
 * @param {string} props.token
 */
export const DuelReviewModal = ({ isOpen, onClose, challengeId, token }) => {
  const { data: review, isLoading, isError } = useChallengeReview({
    token,
    challengeId,
    enabled: isOpen && !!challengeId && !!token,
  });

  if (!isOpen) return null;

  const result = review?.result;

  return (
    <div
      className="duel-review-modal-overlay"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.75)",
        display: "flex",
        justifyContent: "center",
        overflowY: "auto",
        zIndex: 9999,
      }}
    >
      <div className="duel-review-modal-content duel-skin-root duel-skin-modal-box">
        {/* Radar backdrop */}
        <div className="duel-skin-radar-field">
          <div className="duel-skin-radar-sweep"></div>
          <div className="duel-skin-scanline"></div>
        </div>

        {/* Brandline */}
        <div className="duel-skin-brandline" style={{ justifyContent: "center", marginBottom: "16px" }}>
          <div className="duel-skin-mark">
            <span>{brand.mark}</span>
          </div>
          <small>{brand.tagline}</small>
        </div>

        {/* Close Button */}
        <button
          className="duel-review-modal-close duel-skin-close-btn"
          onClick={onClose}
          aria-label={labels.closeAction}
        >
          <CloseIcon />
        </button>

        <h3 className="duel-skin-hero-face" style={{ fontSize: "20px", marginBottom: "4px" }}>
          {labels.reviewHeading}
        </h3>
        <p className="duel-skin-progress-line" style={{ marginBottom: "16px" }}>
          Operativo #{challengeId}
        </p>

        {isLoading && (
          <div style={{ padding: "40px 0", color: "var(--duel-skin-text-dim)" }}>
            <span className="duel-skin-blip" style={{ marginRight: "8px" }}></span>
            Cargando acta del operativo...
          </div>
        )}

        {isError && (
          <div className="duel-skin-error-banner">
            ⚠️ No se pudo obtener el acta del operativo #{challengeId}.
          </div>
        )}

        {review && (
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px 16px",
                marginBottom: "16px",
                border: "1px solid var(--duel-skin-line)",
                borderRadius: "4px",
                background: "var(--duel-skin-panel)",
              }}
            >
              <div style={{ textAlign: "left" }}>
                <div className="duel-skin-callsign">{review.rival?.fullname || "Rival"}</div>
                <div className="duel-skin-time">
                  {review.mytimetaken ? `Tiempo: ${(review.mytimetaken / 1000).toFixed(1)}s` : ""}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <span
                  style={{
                    fontFamily: "ui-monospace, monospace",
                    fontSize: "16px",
                    fontWeight: "bold",
                    color: "var(--duel-skin-text)",
                  }}
                >
                  {review.myscore} - {review.opponentscore}
                </span>
                {result && (
                  <span className={`duel-skin-result-chip duel-skin-result-chip--${result}`}>
                    {RESULT_LABEL[result] || result}
                  </span>
                )}
              </div>
            </div>

            <div className="duel-skin-review-list">
              {(review.questions || []).map((q, idx) => (
                <div key={q.questionid || idx} className="duel-skin-review-item">
                  <div className="duel-skin-q-text">
                    {q.slot || idx + 1}. {q.prompt}
                  </div>
                  <div
                    className="duel-skin-answer"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      marginTop: "6px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span>Tu respuesta:</span>
                    <b>{q.myanswer || labels.noAnswerLabel}</b>
                    {q.myiscorrect ? (
                      <span
                        style={{
                          color: "var(--duel-skin-success)",
                          display: "inline-flex",
                          alignItems: "center",
                          fontSize: "12px",
                          marginLeft: "6px",
                        }}
                      >
                        <CheckCircleOutlineIcon style={{ fontSize: "16px", marginRight: "2px" }} />
                        +{q.mymark} pts
                      </span>
                    ) : (
                      <span
                        style={{
                          color: "var(--duel-skin-danger)",
                          display: "inline-flex",
                          alignItems: "center",
                          fontSize: "12px",
                          marginLeft: "6px",
                        }}
                      >
                        <HighlightOffIcon style={{ fontSize: "16px", marginRight: "2px" }} />
                        0 pts
                      </span>
                    )}
                  </div>
                  {!q.myiscorrect && q.rightanswer && (
                    <div
                      className="duel-skin-time"
                      style={{ color: "var(--duel-skin-gold)", marginTop: "4px" }}
                    >
                      Respuesta correcta: <b>{q.rightanswer}</b>
                    </div>
                  )}
                  {q.opponentanswer && (
                    <div className="duel-skin-time" style={{ marginTop: "4px" }}>
                      Rival ({review.rival?.fullname || "Rival"}): <b>{q.opponentanswer}</b>
                      {" "}({q.opponentiscorrect ? `+${q.opponentmark} pts` : "0 pts"})
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <button
          className="duel-skin-again-btn"
          onClick={onClose}
          style={{ marginTop: "8px" }}
        >
          {labels.closeAction}
        </button>
      </div>
    </div>
  );
};

export default DuelReviewModal;
